// CWallCounter — 左下角剩餘牌數計數 UI
// 從原本的 CInfoBar 拆出（頂部資訊條已移除），只保留「蓋牌 + x + 數字」這組顯示。
//
// 計時邏輯（addEvent / countdown）與輪到誰、莊家風、圈風等資訊已從本元件移除；
// 本元件對外只曝露 setRemainingWall(remaining) 用來更新數字。
//
// === 使用圖片 ===
// - public/assets/tiles/tile-back-down.png（key: 'tile-back-down'）— 剩牌計數蓋牌
// - public/assets/ui/word-x.png（key: WORD_X_KEY）— 剩牌計數「x」
// - public/assets/ui/number-4.png（key: NUMBER_4_KEY，1×11 spritesheet）— 剩牌計數數字 0–9

import type Phaser from 'phaser';

import { LOGICAL_HEIGHT } from '@config/game-config';
import { NUMBER_4_FRAME_WIDTH, NUMBER_4_KEY, WORD_X_KEY } from '@scenes/boot-scene';

// ---- 剩牌計數 (左下角) ----
const WALL_COUNTER_ANCHOR_X = 120; // 整組計數最左邊 X（蓋牌的左緣）
const WALL_COUNTER_ANCHOR_Y_FROM_BOTTOM = 170; // 整組 Y 從畫面底部上推的距離
const WALL_COUNTER_TILE_SCALE = 0.7; // 蓋牌縮放
const WALL_COUNTER_DIGIT_SCALE = 0.8; // 「x」字縮放（word-x 為原生解析度）
// number-4 已 Upscayl 2× 放大，frame 變兩倍 → 數字 scale 取 word-x 的一半，維持原顯示尺寸
const WALL_COUNTER_NUMBER_SCALE = WALL_COUNTER_DIGIT_SCALE / 2; // 數字縮放（補償 2× 素材）
const WALL_COUNTER_TILE_TO_X_GAP = 10; // 蓋牌右緣 → 「x」左緣 的間距（post-scale px）
const WALL_COUNTER_X_TO_DIGIT_GAP = 4; // 「x」右緣 → 第一個數字左緣 的間距
const WALL_COUNTER_DIGIT_GAP = -2; // 數字之間間距（負值 = 稍微重疊）
const WALL_COUNTER_X_Y_OFFSET = 12; // 「x」相對 counterY 的 Y 偏移
const WALL_COUNTER_DIGIT_Y_OFFSET = 14; // 數字相對 counterY 的 Y 偏移

/**
 * 左下角剩餘牌數計數 UI 元件。
 * 持有一個 Phaser Container 來放數字 sprite，每次 setRemainingWall 時整批重畫。
 */
export class CWallCounter {
  private readonly scene: Phaser.Scene;
  // 數字組 Container：建構時擺到 (左緣 X, 中心 Y)，子 sprite 用相對座標
  private readonly digits: Phaser.GameObjects.Container;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    const counterY = LOGICAL_HEIGHT - WALL_COUNTER_ANCHOR_Y_FROM_BOTTOM;
    const tileTex = scene.textures.get('tile-back-down').getSourceImage();
    const tileNativeW = tileTex.width; // 63
    const tileScaledW = tileNativeW * WALL_COUNTER_TILE_SCALE;

    // 蓋牌（最左）
    scene.add
      .image(WALL_COUNTER_ANCHOR_X, counterY, 'tile-back-down')
      .setOrigin(0, 0.5)
      .setScale(WALL_COUNTER_TILE_SCALE);

    // 「x」字符
    const xLeftX = WALL_COUNTER_ANCHOR_X + tileScaledW + WALL_COUNTER_TILE_TO_X_GAP;
    const wordXScaledW = NUMBER_4_FRAME_WIDTH * WALL_COUNTER_NUMBER_SCALE; // word-x 與單一數字同寬
    scene.add
      .image(xLeftX, counterY + WALL_COUNTER_X_Y_OFFSET, WORD_X_KEY)
      .setOrigin(0, 0.5)
      .setScale(WALL_COUNTER_DIGIT_SCALE);

    // 數字 container（右側，內容由 setRemainingWall 動態填）
    const digitsLeftX = xLeftX + wordXScaledW + WALL_COUNTER_X_TO_DIGIT_GAP;
    const digitsCenterY = counterY + WALL_COUNTER_DIGIT_Y_OFFSET;
    this.digits = scene.add.container(digitsLeftX, digitsCenterY);
  }

  /** 用 number-4 spritesheet 重繪剩牌數字。 */
  setRemainingWall(remaining: number): void {
    this.digits.removeAll(true);
    const digitW = NUMBER_4_FRAME_WIDTH * WALL_COUNTER_NUMBER_SCALE;
    const digits = String(Math.max(0, Math.floor(remaining))).split('');
    let cursorX = 0;
    for (const d of digits) {
      const frame = Number(d); // '0'–'9' → 0–9（number-4 spritesheet frame index）
      const sprite = this.scene.add
        .image(cursorX, 0, NUMBER_4_KEY, frame)
        .setOrigin(0, 0.5)
        .setScale(WALL_COUNTER_NUMBER_SCALE);
      this.digits.add(sprite);
      cursorX += digitW + WALL_COUNTER_DIGIT_GAP;
    }
  }
}
